import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Container,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CasinoOutlinedIcon from '@mui/icons-material/CasinoOutlined';
import LandscapeIcon from '@mui/icons-material/Landscape';
import LoopIcon from '@mui/icons-material/Loop';
import CycleCard from '../components/shapeup/CycleCard';
import CycleDialog from '../components/shapeup/CycleDialog';
import { candidateFromOrgMember } from '../components/shapeup/memberRoles';
import { useWorkspace } from '../context/WorkspaceContext';
import { getOrganizationMembers } from '../utils/organizationsApi';
import {
  archiveCycle,
  createCycle,
  deleteCycle,
  getCycleHill,
  getCycles,
  getPitches,
  restoreCycle,
} from '../utils/shapeUpApi';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`cycles-tabpanel-${index}`} aria-labelledby={`cycles-tab-${index}`}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `cycles-tab-${index}`,
    'aria-controls': `cycles-tabpanel-${index}`,
  };
}

// Tabs are fixed positions; the buckets below decide which cycles land in each.
const TAB_PREVIOUS = 0;
const TAB_CURRENT = 1;
const TAB_FUTURE = 2;
const TAB_ARCHIVED = 3;

// An archived cycle is pulled out of the lifecycle buckets entirely, so it never
// clutters the tab someone is working in and only shows under Archived.
const bucketOf = (cycle) => {
  if (cycle.archivedAt) return TAB_ARCHIVED;
  if (cycle.status === 'complete') return TAB_PREVIOUS;
  if (cycle.status === 'active' || cycle.status === 'cooldown') return TAB_CURRENT;
  if (cycle.status === 'planning') return TAB_FUTURE;
  return null;
};

/**
 * A grid of cycles split into what has shipped, what is running, and what is
 * still being bet on. Each card deep-links into the hill chart; the future tab
 * pulls the bets already placed in planning cycles alongside the betting table.
 */
const CyclesPage = () => {
  const { organizations, currentOrganization, teams, refreshTeams } = useWorkspace();

  const [organizationId, setOrganizationId] = useState('');
  const [teamId, setTeamId] = useState('');
  const [cycles, setCycles] = useState([]);
  const [orgCapabilities, setOrgCapabilities] = useState(null);
  const [tab, setTab] = useState(TAB_CURRENT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cycleDialogOpen, setCycleDialogOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [toast, setToast] = useState(null);

  // The future tab is the only one that costs a hill fetch per planning cycle,
  // so it stays untouched until someone opens it. Cleared whenever the org or
  // team filter changes so it reloads against the new scope.
  const [future, setFuture] = useState(null);
  const [futureLoading, setFutureLoading] = useState(false);
  const futureLoadedRef = useRef(false);

  useEffect(() => {
    if (currentOrganization?.id) {
      setOrganizationId(currentOrganization.id);
    } else if (organizations.length > 0) {
      setOrganizationId(organizations[0].id);
    } else {
      setLoading(false);
    }
  }, [currentOrganization, organizations]);

  // keepTab leaves the active tab alone: an in-place refresh after archiving or
  // deleting should not yank someone off the tab they are managing.
  const loadCycles = useCallback(async (orgId, team, { keepTab = false } = {}) => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    // Any prior future work no longer matches the scope being loaded.
    setFuture(null);
    futureLoadedRef.current = false;
    try {
      const data = await getCycles({ organizationId: orgId, teamId: team || undefined, includeArchived: true });
      const list = data.cycles || [];
      setCycles(list);
      setOrgCapabilities(data.capabilities || null);

      // Land on the tab most likely to have something to look at: current work
      // first, then future bets, then the archive.
      if (!keepTab) {
        const hasCurrent = list.some(c => bucketOf(c) === TAB_CURRENT);
        const hasFuture = list.some(c => bucketOf(c) === TAB_FUTURE);
        setTab(hasCurrent ? TAB_CURRENT : hasFuture ? TAB_FUTURE : TAB_PREVIOUS);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load cycles.');
      setCycles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (organizationId) loadCycles(organizationId, teamId);
  }, [organizationId, teamId, loadCycles]);

  // Who could be put on a cycle. Loaded once per organization rather than when
  // the dialog opens, so the picker is populated the moment it appears.
  useEffect(() => {
    if (!organizationId) {
      setCandidates([]);
      return;
    }
    getOrganizationMembers(organizationId)
      .then(members => setCandidates((members || []).map(candidateFromOrgMember)))
      .catch(() => setCandidates([]));
  }, [organizationId]);

  const previous = cycles.filter(c => bucketOf(c) === TAB_PREVIOUS);
  const current = cycles.filter(c => bucketOf(c) === TAB_CURRENT);
  const planning = cycles.filter(c => bucketOf(c) === TAB_FUTURE);
  const archived = cycles.filter(c => bucketOf(c) === TAB_ARCHIVED);

  // The Archived tab only exists while something is in it, so once the last
  // archived cycle is restored or deleted, step off it to avoid pointing at a
  // tab that is no longer rendered.
  useEffect(() => {
    if (tab === TAB_ARCHIVED && archived.length === 0 && !loading) {
      setTab(TAB_CURRENT);
    }
  }, [tab, archived.length, loading]);

  const loadFuture = useCallback(async () => {
    setFutureLoading(true);
    try {
      const [hills, pitchData] = await Promise.all([
        Promise.all(planning.map(cycle => getCycleHill(cycle.id).catch(() => null))),
        getPitches({ organizationId, teamId: teamId || undefined, status: 'ready' }).catch(() => ({ pitches: [] })),
      ]);
      const cyclesWithBets = planning.map((cycle, i) => ({
        cycle,
        bets: hills[i]?.bets || [],
      }));
      setFuture({ cyclesWithBets, pitches: pitchData.pitches || [] });
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load future bets.');
      setFuture({ cyclesWithBets: [], pitches: [] });
    } finally {
      setFutureLoading(false);
    }
  }, [planning, organizationId, teamId]);

  // Fetch the future tab the first time it is shown for the current scope.
  useEffect(() => {
    if (tab === TAB_FUTURE && !loading && !futureLoadedRef.current) {
      futureLoadedRef.current = true;
      loadFuture();
    }
  }, [tab, loading, loadFuture]);

  const handleOrgChange = async (e) => {
    const orgId = e.target.value;
    setOrganizationId(orgId);
    setTeamId('');
    await refreshTeams(orgId);
  };

  const handleCreateCycle = async (payload) => {
    const cycle = await createCycle({
      organizationId,
      ...(teamId ? { teamId } : {}),
      ...payload,
    });
    await loadCycles(organizationId, teamId);
    setToast({ severity: 'success', message: `Created ${cycle.name}` });
  };

  const handleArchive = async (cycle) => {
    try {
      await archiveCycle(cycle.id);
      await loadCycles(organizationId, teamId, { keepTab: true });
      setToast({ severity: 'success', message: `Archived ${cycle.name}` });
    } catch (err) {
      setToast({ severity: 'error', message: err.response?.data?.message || 'Could not archive the cycle.' });
    }
  };

  const handleRestore = async (cycle) => {
    try {
      await restoreCycle(cycle.id);
      await loadCycles(organizationId, teamId, { keepTab: true });
      setToast({ severity: 'success', message: `Restored ${cycle.name}` });
    } catch (err) {
      setToast({ severity: 'error', message: err.response?.data?.message || 'Could not restore the cycle.' });
    }
  };

  const handleDelete = async (cycle) => {
    try {
      await deleteCycle(cycle.id);
      await loadCycles(organizationId, teamId, { keepTab: true });
      setToast({ severity: 'success', message: `Deleted ${cycle.name}` });
    } catch (err) {
      setToast({ severity: 'error', message: err.response?.data?.message || 'Could not delete the cycle.' });
    }
  };

  const teamName = (id) => teams.find(t => t.id === id)?.name;
  const orgCaps = orgCapabilities || { canEdit: true, canDelete: true };

  const renderGrid = (list, emptyTitle, emptyBody) => {
    if (loading) {
      return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
          {[0, 1, 2].map(i => (
            <Skeleton key={i} variant="rectangular" height={140} sx={{ borderRadius: 2 }} />
          ))}
        </Box>
      );
    }
    if (list.length === 0) {
      return (
        <Paper elevation={2} sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>{emptyTitle}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460, mx: 'auto' }}>
            {emptyBody}
          </Typography>
        </Paper>
      );
    }
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3, alignItems: 'stretch' }}>
        {list.map(cycle => (
          <CycleCard
            key={cycle.id}
            cycle={cycle}
            organizationId={organizationId}
            teamName={teamName(cycle.teamId)}
            canManage={orgCaps.canDelete}
            onArchive={handleArchive}
            onRestore={handleRestore}
            onDelete={handleDelete}
          />
        ))}
      </Box>
    );
  };

  const renderFuture = () => {
    if (loading || futureLoading || !future) {
      return <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />;
    }

    const cyclesWithBets = future.cyclesWithBets.filter(entry => entry.bets.length > 0);
    const { pitches } = future;

    if (cyclesWithBets.length === 0 && pitches.length === 0) {
      return (
        <Paper elevation={2} sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>Nothing lined up yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 460, mx: 'auto' }}>
            Future bets show up here once you place them in a planning cycle, or shape a pitch and
            put it on the betting table.
          </Typography>
          <Button variant="outlined" component={RouterLink} to="/shape-up/pitches" startIcon={<CasinoOutlinedIcon />}>
            Go to the betting table
          </Button>
        </Paper>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {cyclesWithBets.map(({ cycle, bets }) => (
          <Paper key={cycle.id} elevation={2} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
              <Link
                component={RouterLink}
                to={`/shape-up?org=${organizationId}&cycle=${cycle.id}`}
                variant="h6"
                underline="hover"
                color="inherit"
                sx={{ fontWeight: 700 }}
              >
                {cycle.name}
              </Link>
              <Chip size="small" variant="outlined" label="Planning" />
              {cycle.teamId && teamName(cycle.teamId) && (
                <Chip size="small" variant="outlined" label={teamName(cycle.teamId)} />
              )}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {bets.map(bet => (
                <Box key={bet.id} sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{bet.name}</Typography>
                  {bet.appetite && (
                    <Chip size="small" variant="outlined" label={bet.appetite === 'small' ? 'Small batch' : 'Big batch'} />
                  )}
                </Box>
              ))}
            </Box>
          </Paper>
        ))}

        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Betting table</Typography>
            <Button size="small" component={RouterLink} to="/shape-up/pitches" startIcon={<CasinoOutlinedIcon />}>
              Open the betting table
            </Button>
          </Box>
          {pitches.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nothing on the table. Shape a pitch to line up work for a future cycle.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {pitches.map(pitch => (
                <Paper key={pitch.id} variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{pitch.title}</Typography>
                    {pitch.appetite && (
                      <Chip size="small" variant="outlined" label={pitch.appetite === 'small' ? 'Small batch' : 'Big batch'} />
                    )}
                    {pitch.isStale && <Chip size="small" color="warning" variant="outlined" label="Stale" />}
                  </Box>
                  {pitch.problem && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {pitch.problem}
                    </Typography>
                  )}
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  if (!loading && organizations.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <LandscapeIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="h5" gutterBottom>Cycles need an organization</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Cycles and bets belong to an organization so your team can share them.
          </Typography>
          <Button variant="contained" component={RouterLink} to="/organizations">
            Set up an organization
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <LoopIcon fontSize="small" />
          Cycles
        </Typography>
      </Breadcrumbs>

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" gutterBottom>Cycles</Typography>
            <Typography variant="body1" color="text.secondary">
              What has shipped, what is running now, and what you are lining up to bet on.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {organizations.length > 1 && (
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Organization</InputLabel>
                <Select value={organizationId} label="Organization" onChange={handleOrgChange}>
                  {organizations.map(org => (
                    <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {teams.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Team</InputLabel>
                <Select value={teamId} label="Team" onChange={(e) => setTeamId(e.target.value)}>
                  <MenuItem value="">All teams</MenuItem>
                  {teams.map(team => (
                    <MenuItem key={team.id} value={team.id}>{team.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {orgCaps.canEdit && (
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setCycleDialogOpen(true)}>
                New cycle
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper elevation={2}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="fullWidth" aria-label="cycles tabs">
            <Tab label={`Previous (${previous.length})`} {...a11yProps(TAB_PREVIOUS)} />
            <Tab label={`Current (${current.length})`} {...a11yProps(TAB_CURRENT)} />
            <Tab label={`Future bets (${planning.length})`} {...a11yProps(TAB_FUTURE)} />
            {archived.length > 0 && (
              <Tab label={`Archived (${archived.length})`} {...a11yProps(TAB_ARCHIVED)} />
            )}
          </Tabs>
        </Box>

        <Box sx={{ p: 3 }}>
          <TabPanel value={tab} index={TAB_PREVIOUS}>
            {renderGrid(
              previous,
              'No completed cycles yet',
              'Cycles land here once you close them, so you can look back at what shipped and what it cost.',
            )}
          </TabPanel>

          <TabPanel value={tab} index={TAB_CURRENT}>
            {renderGrid(
              current,
              'Nothing running right now',
              'An active or cool-down cycle shows here. Start a cycle to begin placing bets.',
            )}
          </TabPanel>

          <TabPanel value={tab} index={TAB_FUTURE}>
            {renderFuture()}
          </TabPanel>

          <TabPanel value={tab} index={TAB_ARCHIVED}>
            {renderGrid(
              archived,
              'Nothing archived',
              'Archive a cycle from its ⋮ menu to hide it here without deleting its bets. You can restore it any time.',
            )}
          </TabPanel>
        </Box>
      </Paper>

      <CycleDialog
        open={cycleDialogOpen}
        onClose={() => setCycleDialogOpen(false)}
        onSubmit={handleCreateCycle}
        candidates={candidates}
        recentCycles={cycles}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">
            {toast.message}
          </Alert>
        ) : null}
      </Snackbar>
    </Container>
  );
};

export default CyclesPage;
